import React, { useState, useRef, useMemo } from 'react'
import { Input, Button, Notification, toast } from 'components/ui'

import { HiOutlineSearch, HiOutlineUser, HiX } from 'react-icons/hi'
import { useSelector, useDispatch } from 'react-redux'
import AvatarProfile from 'components/ui/Avatar/AvatarProfile'
import { useTranslation } from 'react-i18next'
import { apiPutTicket } from 'services/TicketsService'
import { deleteDuplicateDataToFinal, searchInArray, showMessageError } from 'utils/storeHube/helpers'
import { setSelectedTicket, updateTicketList } from '../store/dataSlice'
import { statusResponse } from 'constants/apiResponse.constant'
import { RESPONSIBLE_LABELS } from 'constants/tickets.constant'
import { Loading } from 'components/shared'

const NEW_TICKET = 1
const ASSIGNED = 2

const ChangeOperatorContent = () => {

	const inputRef = useRef()

	const { t } = useTranslation()
	const dispatch = useDispatch()

	const getRoleLabel = (role) => {
		if (role === 'store_managers') {
			const storeAlias = t('store_alias', { defaultValue: 'tienda' }).toLowerCase()
			return t('store_manager_alias', { storeAlias, defaultValue: t('store_manager') })
		}
		return t(RESPONSIBLE_LABELS[role])
	}

	const { selectedTicket, selectedTicketId, usersAllowed, loadingUsersAllowed, ticketList, availableRoles, responsableMap } = useSelector(state => state.ticketList.data)

    const userLogin = useSelector(state => state.auth.user)

	const [loading, setLoading] = useState(false)
	const [beingOperator, setBeingOperator] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')

	const operatorList = useMemo(() => {
		if (!searchQuery) return usersAllowed
		return searchInArray(searchQuery, usersAllowed)
	}, [searchQuery, usersAllowed])

	const onSearch = (e) => {
		setSearchQuery(e.target.value.toLowerCase())
	}

	const isOperator = (id) => {
		const { operator } = selectedTicket.props
		return operator ? operator.id === id : false
	}

	const getRoleUser = (role) => {
		const roleUsers = responsableMap[role]
		if (!roleUsers?.length) {
			return null
		}
		return usersAllowed.find(u => roleUsers.some(ru => ru.id === u.id)) || roleUsers[0]
	}

	const getRoleUserName = (role) => {
		const user = getRoleUser(role)
		return user ? `${user.name} ${user.lastname}` : null
	}

	const roleMatchesSearch = (role) => {
		const query = searchQuery.trim()
		if (!query) {
			return true
		}

		const roleLabel = (getRoleLabel(role) || '').toLowerCase()
		const roleUser = getRoleUser(role)
		const roleUserText = `${roleUser?.name || ''} ${roleUser?.lastname || ''} ${roleUser?.email || ''}`.toLowerCase()

		return roleLabel.includes(query) || roleUserText.includes(query)
	}

	const filteredRoles = availableRoles.filter(roleMatchesSearch)

	const isRoleOperator = (role) => {
		const { operator } = selectedTicket.props
		return operator && responsableMap[role] ? responsableMap[role].some(u => u.id === operator.id) : false
	}

	const onChangeOperator = async (operator) => {
		const isAssigningSelf = operator === userLogin.id
		const isNewTicket = selectedTicket?.props?.ticket_status?.id === NEW_TICKET

		setBeingOperator(operator)
		setLoading(true)
		try {
			const updateData = { operator_id: operator }
			if (isAssigningSelf && isNewTicket) {
				updateData.ticket_status_id = ASSIGNED
			}

			const { data, status } = await apiPutTicket(selectedTicketId, updateData)

			if (status === statusResponse.SUCCESS) {
				const isAssignedToMe = data.data.props?.operator?.id === userLogin.id

				let newTicketList
				if (isAssignedToMe) {
					// Self-assign: update in-place (keep position)
					newTicketList = ticketList.map(ticket =>
						ticket.id === data.data.id ? data.data : ticket
					)
				} else {
					// Assign to other: remove and append to end
					newTicketList = deleteDuplicateDataToFinal(ticketList, data.data)
				}

				dispatch(updateTicketList(newTicketList))
				dispatch(setSelectedTicket(data.data))
				toast.push(
					<Notification title={t('updated')} type="success" duration={2500}>
						{isAssigningSelf && isNewTicket
							? `${t('ticket_alias')} ${t('in_progress').toLowerCase()}`
							: t('ticket_successAssigned')
						}
					</Notification>
				)
			}

		} catch (error) {
			showMessageError(error?.response?.data?.message || error.message)
		}
		setBeingOperator(false)
		setLoading(false)
	}

	return (
		<Loading
			loading={loadingUsersAllowed}
			type="cover"
			className="flex-1 min-h-0"
		>
			<div className="h-full min-h-0 flex flex-col">
				<div className="bg-white dark:bg-gray-800 flex-shrink-0 py-3 border-b border-gray-200 dark:border-gray-600">
					<Input
						ref={inputRef}
						prefix={<HiOutlineSearch className="text-lg" />}
						suffix={searchQuery && (
							<HiX
								className="text-lg cursor-pointer hover:text-red-500"
								onClick={() => setSearchQuery('')}
							/>
						)}
						value={searchQuery}
						onChange={onSearch}
						placeholder={t('search')}
					/>
				</div>
				<div className="pt-4 pr-1 flex-1 min-h-0 overflow-y-auto">
						{filteredRoles.length > 0 && (
							<div className="mb-4">
								<p className="font-semibold uppercase text-xs mb-2">{t('responsibles', { defaultValue: 'Responsables' })}</p>
								{filteredRoles.map(role => {
								const roleUserName = getRoleUserName(role)
								return (
									<div key={role} className="py-2 pr-5 rounded-lg flex items-center justify-between">
										<div className="flex items-center gap-2">
											<span className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600">
												<HiOutlineUser className="text-base" />
											</span>
											<div>
												<p className="heading-text font-bold">{getRoleLabel(role)}</p>
												{roleUserName && <p className="text-xs">{roleUserName}</p>}
											</div>
										</div>
										{isRoleOperator(role)
											? <span className="heading-text font-bold">{t('operator')}</span>
											: <Button
												size="xs"
												onClick={() => onChangeOperator(role)}
												loading={loading && beingOperator === role}>
												{t('assign')}
											</Button>
										}
									</div>
								)})}
								<hr className="my-3" />
							</div>
						)}
						{operatorList.length > 0 ? (
							<>
								<p className="font-semibold uppercase text-xs mb-4">{operatorList.length} {t('ticket_available_operators')}</p>
								<div>
									{operatorList.map(user => (
										<div key={user.id} className="py-3 pr-5 rounded-lg flex items-center justify-between">
											<div className="flex items-center gap-2">
												<AvatarProfile className={'mr-1 rtl:ml-1 cursor-pointer'} user={user} />
												<div>
													<p className="heading-text font-bold">{user.name} {user.lastname}</p>
													<p>{user.email}</p>
												</div>
											</div>
											{isOperator(user.id)
												? <span className="heading-text font-bold">{t('operator')}</span>
												: <Button
													size="xs"
													onClick={() => onChangeOperator(user.id)}
													loading={loading && beingOperator === user.id}>
													{t('assign')}
												</Button>
											}
										</div>
									))}
								</div>
							</>
						) : filteredRoles.length === 0 && (
							<p className="text-center text-gray-400 py-4">{t('no_results', { defaultValue: 'Sin resultados' })}</p>
						)}
				</div>
			</div>
		</Loading>
	)
}

export default ChangeOperatorContent
